import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { EligibleToEditStatuses, FulfillmentPlanOrderGridStatus } from '../../enums/fulfillmentplan-order-grid-status';
import PropTypes from 'prop-types';
import { ChildrenPropType } from '../../prop-types/children';
import { GuidService } from 'ui-components/services/guid-service';
import { FulfillmentPlanPropType } from '../../prop-types/fulfillment-plan';
import { useSaveSplitShip } from './use-save-split-ship';
import { useSortItemsTable } from './use-sort-items-table';

const SplitShipModalContext = createContext({});

export const useSplitShipModalContext = () => useContext(SplitShipModalContext);

export const isFulfillmentPlanStatusActionable = (status) => {
  return EligibleToEditStatuses.includes(status);
};

const findIndexOfRowToMergeWith = (items, itemRow, fulfillmentPlanId) =>
  items.findIndex((x) => x.dataRowId !== itemRow.dataRowId && x.fulfillmentPlanId === fulfillmentPlanId && x.mergeKey === itemRow.mergeKey);

const SplitShipModalContextProvider = ({ children, initialFulfillmentPlans, initialTableData, closeModal, salesOrderId }) => {
  const [fulfillmentPlans, setFulfillmentPlans] = useState(initialFulfillmentPlans);
  const [tableData, setTableData] = useState(initialTableData);
  const [hasMadeChanges, setHasMadeChanges] = useState(false);
  const [splitRowId, setSplitRowId] = useState('');

  const createNewFulfillmentPlan = useCallback(() => {
    const fulfillmentPlanId = GuidService.newGuid();
    const newFulfillmentPlan = {
      fulfillmentPlanId,
      orderGridStatus: FulfillmentPlanOrderGridStatus.AwaitingShipment.name,
      isNew: true,
    };

    setFulfillmentPlans((prevState) => [...prevState, newFulfillmentPlan]);
    setHasMadeChanges(true);

    return newFulfillmentPlan;
  }, []);

  const moveItemToFulfillmentPlan = useCallback(
    (dataRowId, newFulfillmentPlanId) => {
      const newFpGuid = newFulfillmentPlanId ?? createNewFulfillmentPlan().fulfillmentPlanId;
      setTableData((prevState) => {
        const newState = [...prevState];
        const rowIndex = newState.findIndex((x) => x.dataRowId === dataRowId);
        const row = newState[rowIndex];

        const existingRowWithItemIndex = findIndexOfRowToMergeWith(newState, row, newFpGuid);
        if (existingRowWithItemIndex >= 0) {
          const existingItemRow = newState[existingRowWithItemIndex];
          newState[existingRowWithItemIndex] = {
            ...existingItemRow,
            quantity: existingItemRow.quantity + row.quantity,
          };
          newState.splice(rowIndex, 1);

          return newState;
        }

        newState[rowIndex] = {
          ...row,
          fulfillmentPlanId: newFpGuid,
        };

        return newState;
      });
      setHasMadeChanges(true);
    },
    [createNewFulfillmentPlan],
  );

  const applySplit = useCallback(
    (parentDataRowId, parentQuantity, parentFulfillmentPlanId, childQuantity, childFulfillmentPlanId) => {
      // retrieve existing fulfillmentPlanItemId from initialTableData if it exists else use the parentRow fulfillmentPlanItemId
      const createTableDataRow = (fulfillmentPlanId, parentRow, quantity, dataRowId) => {
        const matchingRowWithInitialTableData = initialTableData.find(
          (x) => x.fulfillmentPlanId === fulfillmentPlanId && x.mergeKey === parentRow.mergeKey,
        );
        const fulfillmentPlanItemId = matchingRowWithInitialTableData
          ? matchingRowWithInitialTableData.fulfillmentPlanItemId
          : parentRow.fulfillmentPlanItemId;

        return {
          ...parentRow,
          fulfillmentPlanItemId,
          dataRowId,
          quantity,
          fulfillmentPlanId,
        };
      };

      setTableData((prevState) => {
        const newState = [...prevState];

        const parentRowIndex = newState.findIndex((x) => x.dataRowId === parentDataRowId);
        const parentRow = newState[parentRowIndex];

        // update parent row
        const indexOfRowToMergeParentWith = findIndexOfRowToMergeWith(newState, parentRow, parentFulfillmentPlanId);
        if (indexOfRowToMergeParentWith >= 0) {
          const existingRow = newState[indexOfRowToMergeParentWith];

          newState[indexOfRowToMergeParentWith] = {
            ...existingRow,
            quantity: existingRow.quantity + parentQuantity,
          };

          newState.splice(parentRowIndex, 1);
        } else {
          newState[parentRowIndex] = createTableDataRow(parentFulfillmentPlanId, parentRow, parentQuantity, parentRow.dataRowId);
        }

        // update child row
        const indexOfRowToMergeChildWith = findIndexOfRowToMergeWith(newState, parentRow, childFulfillmentPlanId);
        if (indexOfRowToMergeChildWith >= 0) {
          const existingRow = newState[indexOfRowToMergeChildWith];

          newState[indexOfRowToMergeChildWith] = {
            ...existingRow,
            quantity: existingRow.quantity + childQuantity,
          };
        } else {
          // otherwise
          // create and insert a new row
          const newRow = createTableDataRow(childFulfillmentPlanId, parentRow, childQuantity, GuidService.newGuid());

          newState.splice(parentRowIndex + 1, 0, newRow);
        }

        return newState;
      });
      setHasMadeChanges(true);
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const { save, isSaving, saveFailed } = useSaveSplitShip({
    closeModal,
    fulfillmentPlans,
    initialTableData,
    salesOrderId,
    tableData,
  });

  const { sort, sortState } = useSortItemsTable(setTableData, fulfillmentPlans);

  const value = useMemo(
    () => ({
      fulfillmentPlans,
      tableData,
      hasMadeChanges,
      createNewFulfillmentPlan,
      moveItemToFulfillmentPlan,
      applySplit,
      splitRowId,
      setSplitRowId,
      save,
      isSaving,
      saveFailed,
      sort,
      sortState,
      initialTableData,
    }),
    [
      fulfillmentPlans,
      tableData,
      hasMadeChanges,
      createNewFulfillmentPlan,
      moveItemToFulfillmentPlan,
      applySplit,
      splitRowId,
      save,
      isSaving,
      saveFailed,
      sort,
      sortState,
      initialTableData,
    ],
  );

  return <SplitShipModalContext.Provider value={value}>{children}</SplitShipModalContext.Provider>;
};

export default SplitShipModalContextProvider;

SplitShipModalContextProvider.propTypes = {
  children: ChildrenPropType.isRequired,
  closeModal: PropTypes.func.isRequired,
  initialFulfillmentPlans: PropTypes.arrayOf(FulfillmentPlanPropType).isRequired,
  initialTableData: PropTypes.arrayOf(FulfillmentPlanPropType).isRequired,
  salesOrderId: PropTypes.string.isRequired,
};
